"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = exports.QuantityPicker = void 0;
var _react = require("@floating-ui/react");
var _icons = require("@ori-ui/icons");
var _material = require("@ori-ui/mui-bundle/material");
var _clsx = _interopRequireDefault(require("clsx"));
var _react2 = require("react");
var _utils = require("../utils");
var _constants = require("./constants");
var _quantityPickerStyled = require("./quantityPickerStyled");
var _useQuantityPicker = require("./useQuantityPicker");
var _useVisualProps = require("./useVisualProps");
var _jsxRuntime = require("react/jsx-runtime");
const QuantityPicker = exports.QuantityPicker = /*#__PURE__*/(0, _react2.forwardRef)((inProps, ref) => {
  const props = (0, _material.useThemeProps)({
    props: inProps,
    name: 'MuiOriQuantityPicker'
  });
  const {
    className,
    component = 'div',
    DecreaseIconButtonProps = {},
    disabled = false,
    expanded,
    expandOnFocus = false,
    height,
    IncreaseIconButtonProps = {},
    InputProps: InputBaseProps = {},
    labelsTranslations = {},
    onChange,
    onClickAway,
    onFocus,
    minValue = _constants.DEFAULT_MIN_VALUE,
    readOnly = false,
    required,
    size = 'medium',
    tooltipMaxText,
    variant = 'contained',
    width,
    maxValue = _constants.DEFAULT_MAX_VALUE,
    maxGroupValue = maxValue,
    tooltipMaxDisabled = false,
    ...otherProps
  } = props;
  const [focused, setFocused] = (0, _react2.useState)(expanded ?? false);
  const {
    groupValue,
    handleBlur,
    handleChange,
    handleKeyDown,
    InputProps,
    onDecrease,
    onIncrease,
    setTooltipMaxOpen,
    value,
    tooltipMaxOpen
  } = (0, _useQuantityPicker.useQuantityPicker)({
    ...props
  });
  const {
    cssProps,
    buttonProps
  } = (0, _useVisualProps.useVisualProps)(props);

  // INFO Memoize ownerState to avoid unnecessary recalculations
  const ownerState = (0, _react2.useMemo)(() => ({
    ...props,
    cssProps,
    disabled,
    expandOnFocus,
    focused,
    readOnly,
    size,
    variant
  }), [props, cssProps, disabled, expandOnFocus, focused, readOnly, size, variant]);
  (0, _react2.useEffect)(() => {
    if (typeof expanded === 'boolean') {
      setFocused(expanded);
    }
  }, [expanded]);
  const handleClickAway = (0, _react2.useCallback)(() => {
    if (typeof expanded === 'boolean') {
      return;
    }
    setFocused(false);
  }, [expanded]);
  const handleFocus = (0, _react2.useCallback)(() => {
    if (typeof expanded === 'boolean') {
      return;
    }
    setFocused(true);
  }, [expanded]);
  const handleFocusForked = (0, _utils.useForkCallback)(handleFocus, onFocus);
  const handleClickAwayForked = (0, _utils.useForkCallback)(handleClickAway, onClickAway);
  const classes = (0, _quantityPickerStyled.useUtilityClasses)(ownerState);
  const inputRef = (0, _react2.useRef)(null);

  // INFO Use state to hold the arrow element for floating-ui
  const [arrowElement, setArrowElement] = (0, _react2.useState)(null);

  // INFO Memoize floating-ui config
  const floatingConfig = (0, _react2.useMemo)(() => ({
    open: tooltipMaxOpen,
    onOpenChange: setTooltipMaxOpen,
    placement: 'top-end',
    strategy: 'absolute',
    middleware: [(0, _react.offset)({
      mainAxis: 15,
      alignmentAxis: -10
    }), (0, _react.shift)(), (0, _react.flip)(), (0, _react.size)({
      apply({
        elements
      }) {
        Object.assign(elements.floating.style, {
          maxWidth: `calc(${cssProps.holder.width} - 10px)`
        });
      }
    }), (0, _react.arrow)({
      element: arrowElement
    })],
    whileElementsMounted: _react.autoUpdate
  }), [tooltipMaxOpen, setTooltipMaxOpen, cssProps.holder.width, arrowElement]);
  const {
    refs: {
      setReference,
      setFloating
    },
    floatingStyles,
    context
  } = (0, _react.useFloating)(floatingConfig);
  const max = Math.min(maxValue, maxGroupValue);
  const maxReached = value >= max || groupValue >= maxGroupValue;
  const dismiss = (0, _react.useDismiss)(context);
  const role = (0, _react.useRole)(context, {
    role: 'alertdialog'
  });
  const {
    getFloatingProps
  } = (0, _react.useInteractions)([dismiss, role]);
  const {
    styles: transitionStyles
  } = (0, _react.useTransitionStyles)(context, {
    initial: {
      opacity: 0,
      transform: 'scale(0.8)'
    }
  });
  const isTooltipMaxUsable = (0, _react2.useMemo)(() => {
    if (!tooltipMaxDisabled && typeof tooltipMaxText === 'string' && Boolean(tooltipMaxText) && maxValue < _constants.DEFAULT_MAX_VALUE) {
      return true;
    }
    return false;
  }, [maxValue, tooltipMaxDisabled, tooltipMaxText]);
  const showTooltip = (0, _react2.useCallback)(() => {
    setTooltipMaxOpen(true);
  }, [setTooltipMaxOpen]);

  // INFO preparing input props
  const {
    inputProps,
    ...otherInputProps
  } = InputProps;
  const inputElementProps = (0, _react2.useMemo)(() => ({
    'aria-label': labelsTranslations.inputLabel ?? 'Quantity',
    ...inputProps
  }), [inputProps, labelsTranslations.inputLabel]);

  // INFO when we use tooltip then the button is never disabled (will look disabled and have different onClick behavior)
  const increaseDisableState = isTooltipMaxUsable ? false : disabled || value >= maxValue;
  // INFO we need this state multiple times so set it in one place make sense
  const isTooltipActive = isTooltipMaxUsable && (maxReached || disabled);
  // INFO when we use tooltip and we reached the limit then the tooltip is shown on click, otherwise the onIncrease function is called
  const increaseOnClick = isTooltipActive ? showTooltip : onIncrease;
  // INFO when we use tooltip and we reached the requirements for tooltip then we need different button styles
  const increaseOwnerState = isTooltipActive ? {
    ...ownerState,
    tooltipTrigger: true
  } : ownerState;
  // INFO when we use tooltip the button need to have ref to tooltip
  const increaseRef = (0, _react2.useMemo)(() => isTooltipMaxUsable ? setReference : undefined, [isTooltipMaxUsable, setReference]);
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_material.ClickAwayListener, {
    onClickAway: handleClickAwayForked,
    children: /*#__PURE__*/(0, _jsxRuntime.jsxs)(_quantityPickerStyled.QuantityPickerRoot, {
      ...otherProps,
      ref: ref,
      ownerState: ownerState,
      className: (0, _clsx.default)(classes.root, className),
      as: component,
      onFocus: handleFocusForked,
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_quantityPickerStyled.IconButtonRoot, {
        ownerState: ownerState,
        disabled: disabled || value <= minValue,
        className: (0, _clsx.default)(classes.iconButton, classes.decreaseButton),
        "aria-label": labelsTranslations.decreaseButtonLabel ?? 'Decrease quantity',
        ...buttonProps,
        ...DecreaseIconButtonProps,
        onClick: onDecrease,
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_icons.Remove, {
          fontSize: "inherit"
        })
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_quantityPickerStyled.InputRoot, {
        inputRef: inputRef,
        ownerState: ownerState,
        readOnly: readOnly,
        required: required,
        value: value,
        disabled: disabled,
        className: classes.input,
        ...InputBaseProps,
        ...otherInputProps,
        inputProps: inputElementProps,
        onChange: handleChange,
        onBlur: handleBlur,
        onKeyDown: handleKeyDown
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_quantityPickerStyled.IconButtonRoot, {
        ref: increaseRef,
        disableFocusRipple: isTooltipActive,
        disableRipple: isTooltipActive,
        disableTouchRipple: isTooltipActive,
        ownerState: increaseOwnerState,
        disabled: increaseDisableState,
        className: (0, _clsx.default)(classes.iconButton, classes.increaseButton),
        "aria-label": labelsTranslations.increaseButtonLabel ?? 'Increase quantity',
        ...buttonProps,
        ...IncreaseIconButtonProps,
        onClick: increaseOnClick,
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_icons.Add, {
          fontSize: "inherit"
        })
      }), isTooltipMaxUsable && tooltipMaxOpen ? /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
        ref: setFloating,
        style: {
          ...floatingStyles,
          zIndex: 1
        },
        ...getFloatingProps(),
        "aria-labelledby": "max-value-tooltip-content",
        children: /*#__PURE__*/(0, _jsxRuntime.jsxs)(_quantityPickerStyled.TooltipContainer, {
          style: transitionStyles,
          children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_quantityPickerStyled.TooltipArrow, {
            ref: setArrowElement,
            tipRadius: 2,
            height: 8,
            context: context
          }), /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
            id: "max-value-tooltip-content",
            children: tooltipMaxText
          })]
        })
      }) : null]
    })
  });
});
var _default = exports.default = QuantityPicker;
